import { type CouleurNoyau, NOYAUX, type NoyauCode } from '@rosetta/shared';

/**
 * Display constants of the noyaux. The class names are spelled out in full, never
 * built from the token name, so that Tailwind can see them. Colours are the PMS
 * tokens of `styles.css`: no hex here.
 */
export interface StyleNoyau {
  /** Background of the round dot. */
  pastille: string;
  /**
   * Frame of a checked button. It is what tells a checked button from an unchecked one, so it
   * needs 3:1 against the white page (WCAG 1.4.11): bleu ciel and jaune, which do not, take
   * their `-deep` token here (S14, #5).
   */
  cadre: string;
  /** Light background of a checked button. */
  fond: string;
  /** Background of a checked button under the pointer: the same colour, one notch denser. */
  fondSurvol: string;
  /** Underline of the active tab: the same rule as `cadre`, it is what shows the tab is active. */
  soulignement: string;
  /** Top net of a result card. */
  filet: string;
}

export const STYLES_NOYAU: Readonly<Record<CouleurNoyau, StyleNoyau>> = {
  'bleu-nuit': {
    pastille: 'bg-bleu-nuit',
    cadre: 'border-bleu-nuit',
    fond: 'bg-bleu-nuit-tint',
    fondSurvol: 'hover:bg-bleu-nuit/15',
    soulignement: 'border-bleu-nuit',
    filet: 'border-t-bleu-nuit',
  },
  'bleu-ciel': {
    pastille: 'bg-bleu-ciel',
    cadre: 'border-bleu-ciel-deep',
    fond: 'bg-bleu-ciel-tint',
    fondSurvol: 'hover:bg-bleu-ciel/15',
    soulignement: 'border-bleu-ciel-deep',
    filet: 'border-t-bleu-ciel',
  },
  vert: {
    pastille: 'bg-vert',
    cadre: 'border-vert',
    fond: 'bg-vert-tint',
    fondSurvol: 'hover:bg-vert/15',
    soulignement: 'border-vert',
    filet: 'border-t-vert',
  },
  jaune: {
    pastille: 'bg-jaune',
    cadre: 'border-jaune-deep',
    fond: 'bg-jaune-tint',
    fondSurvol: 'hover:bg-jaune/15',
    soulignement: 'border-jaune-deep',
    filet: 'border-t-jaune',
  },
  rouge: {
    pastille: 'bg-rouge',
    cadre: 'border-rouge',
    fond: 'bg-rouge-tint',
    fondSurvol: 'hover:bg-rouge/15',
    soulignement: 'border-rouge',
    filet: 'border-t-rouge',
  },
  rose: {
    pastille: 'bg-rose',
    cadre: 'border-rose',
    fond: 'bg-rose-tint',
    fondSurvol: 'hover:bg-rose/15',
    soulignement: 'border-rose',
    filet: 'border-t-rose',
  },
};

export function styleNoyau(code: NoyauCode): StyleNoyau {
  return STYLES_NOYAU[NOYAUX[code].couleur];
}

/** "Guerrier cosmique": the long name, capitalised as when it stands alone. */
export function nomLongNoyau(code: NoyauCode): string {
  const { famille, variante } = NOYAUX[code];
  return `${famille.charAt(0).toUpperCase()}${famille.slice(1)} ${variante}`;
}

/** Tooltip and accessible name of a noyau button: the code and the long name. */
export function infobulleNoyau(code: NoyauCode): string {
  const { famille, variante } = NOYAUX[code];
  return `${code} ${famille} ${variante}`;
}
